// Wanneer moet dit af? `!vrijdag`, `!morgen`, `!25-09`.
//
// Een datum in een taak is gewone tekst — dat moet ook, want de export is markdown en daar is
// geen plek voor een veld dat er niet in staat. Je typt hem dus gewoon in de regel:
//
//     [ ] offerte nabellen !vrijdag
//     [ ] btw-aangifte !25-01 09:00
//
// Het uitroepteken is gekozen omdat het geen andere betekenis heeft in onze tekst: `#` is een
// tag, `[[` een link, `@` bewaren we voor als er ooit meer mensen bij komen.
//
// Deze module leest dat stukje en zegt niets over waar het vandaan komt; hij draait daarom
// zowel op de server als in de browser, net als de markdown. Zonder tijd erbij is het negen
// uur 's ochtends: een herinnering die om middernacht afgaat heb je 's ochtends al weggeklikt.

/** Het uur waarop een herinnering zonder tijdstip afgaat. */
export const STANDAARDUUR = 9;

const DAGEN: Record<string, number> = {
  zondag: 0, zo: 0,
  maandag: 1, ma: 1,
  dinsdag: 2, di: 2,
  woensdag: 3, wo: 3,
  donderdag: 4, do: 4,
  vrijdag: 5, vr: 5,
  zaterdag: 6, za: 6
};

function opTijd(datum: Date, uren: number, minuten: number): Date {
  const uit = new Date(datum);
  uit.setHours(uren, minuten, 0, 0);
  return uit;
}

/**
 * Eén woord naar een dag. `null` als het geen datum is — dan blijft het gewone tekst, want een
 * uitroepteken midden in een zin ("!!!") hoort geen herinnering te worden.
 */
export function naarDag(woord: string, nu = new Date()): Date | null {
  const klein = woord.toLowerCase();

  if (klein === "vandaag") return new Date(nu);
  if (klein === "morgen") return new Date(nu.getTime() + 86_400_000);
  if (klein === "overmorgen") return new Date(nu.getTime() + 2 * 86_400_000);
  // Ook naar achteren: "!gisteren" betekent dat het gisteren al af had moeten zijn, en zo staat
  // hij meteen bovenaan met een rood kaartje. Zonder dit bleef het letterlijke tekst.
  if (klein === "gisteren") return new Date(nu.getTime() - 86_400_000);
  if (klein === "eergisteren") return new Date(nu.getTime() - 2 * 86_400_000);

  // Een dagnaam betekent de éérstvolgende keer dat het die dag is, vandaag meegeteld: schrijf
  // je op vrijdagochtend "!vrijdag", dan bedoel je vandaag en niet over een week.
  if (klein in DAGEN) {
    const verschil = (DAGEN[klein] - nu.getDay() + 7) % 7;
    return new Date(nu.getTime() + verschil * 86_400_000);
  }

  // 25-09 of 25-09-2026: dag eerst, zoals je het in Nederland schrijft.
  const nl = klein.match(/^(\d{1,2})-(\d{1,2})(?:-(\d{4}))?$/);
  if (nl) {
    const dag = Number(nl[1]);
    const maand = Number(nl[2]);
    if (dag < 1 || dag > 31 || maand < 1 || maand > 12) return null;
    const jaar = nl[3] ? Number(nl[3]) : nu.getFullYear();
    let datum = new Date(jaar, maand - 1, dag);
    // Zonder jaartal en al voorbij: dan bedoel je volgend jaar. "!25-01" in december is de
    // aangifte van januari, niet die van elf maanden geleden.
    if (!nl[3] && opTijd(datum, 23, 59) < nu) datum = new Date(jaar + 1, maand - 1, dag);
    if (datum.getDate() !== dag || datum.getMonth() !== maand - 1) return null; // 31-02 bestaat niet
    return datum;
  }

  // 2026-09-25: zoals het in de export staat.
  const iso = klein.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (iso) {
    const datum = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
    if (datum.getMonth() !== Number(iso[2]) - 1) return null;
    return datum;
  }

  return null;
}

export type Gelezen = { wanneer: string | null; tekst: string };

/**
 * Haalt `!<dag>` (en een tijd erachter) uit de tekst.
 *
 * Wat teruggegeven wordt is de tekst zónder die markering: in het takenoverzicht staat de datum
 * als los kaartje, en dan zou "!vrijdag" er twee keer staan. In de notitie zelf blijft hij
 * natuurlijk gewoon staan — dat is de bron.
 */
export function leesWanneer(tekst: string, nu = new Date()): Gelezen {
  const treffer = tekst.match(/(^|\s)!([\p{L}\d-]+)(?:\s+(\d{1,2}):(\d{2}))?/u);
  if (!treffer) return { wanneer: null, tekst };

  const dag = naarDag(treffer[2], nu);
  if (!dag) return { wanneer: null, tekst };

  const uren = treffer[3] === undefined ? STANDAARDUUR : Number(treffer[3]);
  const minuten = treffer[4] === undefined ? 0 : Number(treffer[4]);
  if (uren > 23 || minuten > 59) return { wanneer: null, tekst };

  const schoon = (tekst.slice(0, treffer.index) + tekst.slice(treffer.index! + treffer[0].length))
    .replace(/\s{2,}/g, " ")
    .trim();
  return { wanneer: opTijd(dag, uren, minuten).toISOString(), tekst: schoon };
}

/** "vandaag", "morgen", "vrijdag" of "25 sep" — hoe het in het overzicht komt te staan. */
export function alsWoord(wanneer: string, nu = new Date()): string {
  const datum = new Date(wanneer);
  const dagenVerschil = Math.round(
    (opTijd(datum, 0, 0).getTime() - opTijd(nu, 0, 0).getTime()) / 86_400_000
  );
  const tijd = datum.getHours() === STANDAARDUUR && datum.getMinutes() === 0
    ? ""
    : ` ${String(datum.getHours()).padStart(2, "0")}:${String(datum.getMinutes()).padStart(2, "0")}`;

  if (dagenVerschil === 0) return `vandaag${tijd}`;
  if (dagenVerschil === 1) return `morgen${tijd}`;
  if (dagenVerschil === -1) return `gisteren${tijd}`;
  if (dagenVerschil < 0) return datum.toLocaleDateString("nl-NL", { day: "numeric", month: "short" });
  if (dagenVerschil < 7) {
    const namen = ["zondag", "maandag", "dinsdag", "woensdag", "donderdag", "vrijdag", "zaterdag"];
    return `${namen[datum.getDay()]}${tijd}`;
  }
  return datum.toLocaleDateString("nl-NL", { day: "numeric", month: "short" });
}
